import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { getAuditLogMeta, getAuditLogs } from "../../server/Api";
import "../../assets/css/occasion.css";
import "../../assets/css/AuditLog.css";

const ACTION_LABELS = {
  CREATE: "Created",
  UPDATE: "Updated",
  DELETE: "Deleted",
  IMPORT: "Imported",
  LOGIN: "Signed in",
  ACCESS_CHANGE: "Access changed",
  ALLOCATE: "Allocated",
  INVITE: "Invite sent",
};

const ENTITY_LABELS = {
  Guest: "Guest",
  Attendee: "Attendee",
  Occasion: "Occasion",
  Hotel: "Hotel",
  Stay: "Stay",
  Travel: "Travel",
  Seat: "Seat",
  GuestAllocation: "Guest allocation",
  Invite: "Invite",
  User: "User",
  Event: "Event",
  RoleAccess: "Page access",
};

const formatWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** Strip appended field list from summary (shown separately as Changes). */
const cleanSummary = (summary = "") =>
  String(summary)
    .replace(/\s*·\s*[^·]+$/, "")
    .trim() || String(summary || "").trim();

const formatChangeValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    if (value.length === 0) return "—";
    return value
      .map((item) =>
        typeof item === "object" ? formatChangeValue(item) : String(item)
      )
      .join(", ");
  }
  if (typeof value === "object") {
    if (
      "view" in value ||
      "edit" in value ||
      "delete" in value ||
      "specialActions" in value
    ) {
      const flags = [];
      if (value.view) flags.push("View");
      if (value.edit) flags.push("Edit");
      if (value.delete) flags.push("Delete");
      if (value.specialActions) flags.push("Special actions");
      return flags.length ? flags.join(", ") : "None";
    }
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const formatFieldLabel = (field) => {
  if (!field) return "—";
  const map = {
    role: "Role",
    isActive: "Status",
    name: "Name",
    email: "Email",
    pagePermissions: "Page permissions",
    firstName: "First name",
    MiddleName: "Middle name",
    middleName: "Middle name",
    lastName: "Last name",
    OccasionName: "Occasion name",
    OccasionCapacity: "Capacity",
    DressCode: "Dress code",
    InvitationType: "Invitation type",
    RSVPType: "RSVP type",
    RSVP: "RSVP enabled",
    isOccasionActive: "Active",
    hotelName: "Hotel name",
    totalRooms: "Total rooms",
    rooms: "Rooms",
    allowedPages: "Allowed pages",
    hotelId: "Hotel",
    roomIds: "Rooms assigned",
    checkInDate: "Check-in date",
    checkOutDate: "Check-out date",
    foodPreference: "Food preference",
    specialNeeds: "Special needs",
    specialRequest: "Special request",
  };
  if (map[field]) return map[field];
  return String(field)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const getChangedFields = (row) => {
  const fromChanges = Array.isArray(row?.changes)
    ? row.changes.map((c) => c.field).filter(Boolean)
    : [];
  if (fromChanges.length) return [...new Set(fromChanges)];
  if (Array.isArray(row?.meta?.allowedPages)) return ["allowedPages"];
  return [];
};

const entityTypeLabel = (type) => ENTITY_LABELS[type] || type || "Record";

const AuditLog = () => {
  const user = useSelector((state) => state.auth.user);
  const selectedEvent = useSelector((state) => state.auth.selectedEvent);

  const canView = Boolean(
    user?.isSuperAdmin || user?.isAdmin || selectedEvent?.isAdmin
  );

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 1,
  });
  const [meta, setMeta] = useState({
    actions: [],
    entityTypes: [],
    events: [],
  });
  const [selectedId, setSelectedId] = useState(null);

  const [filters, setFilters] = useState({
    q: "",
    action: "",
    entityType: "",
    eventId: selectedEvent?.eventId || "",
    from: "",
    to: "",
  });
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((prev) => {
        if (prev.q === searchInput) return prev;
        setPagination((p) => ({ ...p, page: 1 }));
        return { ...prev, q: searchInput };
      });
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (selectedEvent?.eventId && !user?.isSuperAdmin) {
      setFilters((prev) => ({ ...prev, eventId: selectedEvent.eventId }));
    }
  }, [selectedEvent?.eventId, user?.isSuperAdmin]);

  const loadMeta = useCallback(async () => {
    if (!canView) return;
    try {
      const params = {};
      if (!user?.isSuperAdmin && selectedEvent?.eventId) {
        params.eventId = selectedEvent.eventId;
      } else if (filters.eventId) {
        params.eventId = filters.eventId;
      }
      const res = await axios.get(getAuditLogMeta(params));
      setMeta(res.data?.data || { actions: [], entityTypes: [], events: [] });
    } catch (error) {
      console.error(error);
    }
  }, [canView, user?.isSuperAdmin, selectedEvent?.eventId, filters.eventId]);

  const loadLogs = useCallback(async () => {
    if (!canView) return;
    if (!user?.isSuperAdmin && !selectedEvent?.eventId) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        q: filters.q,
        action: filters.action,
        entityType: filters.entityType,
        from: filters.from,
        to: filters.to,
      };
      if (user?.isSuperAdmin) {
        if (filters.eventId) params.eventId = filters.eventId;
      } else {
        params.eventId = selectedEvent.eventId;
      }

      const res = await axios.get(getAuditLogs(params));
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setRows(list);
      setPagination((prev) => ({
        ...prev,
        ...(res.data?.pagination || {}),
      }));
      setSelectedId((prev) => {
        if (prev && list.some((r) => String(r._id) === String(prev))) return prev;
        return list[0]?._id || null;
      });
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to load audit logs.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [
    canView,
    user?.isSuperAdmin,
    selectedEvent?.eventId,
    pagination.page,
    pagination.limit,
    filters,
  ]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const selected = useMemo(
    () => rows.find((r) => String(r._id) === String(selectedId)) || null,
    [rows, selectedId]
  );

  const onFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  if (!canView) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!user?.isSuperAdmin && !selectedEvent?.eventId) {
    return (
      <div className="container-fluid audit-log-page">
        <div className="occasion-top row g-4 align-items-center">
          <div className="col-lg-5">
            <h2 className="mb-0">Audit Log</h2>
          </div>
        </div>
        <p className="audit-log-empty">
          Select an event to view its audit trail.
        </p>
      </div>
    );
  }

  return (
    <div className="container-fluid audit-log-page">
      <div className="occasion-top row g-4 align-items-center">
        <div className="col-lg-5">
          <h2 className="mb-0">Audit Log</h2>
        </div>
        <div className="col-lg-7">
          <div className="audit-log-top-actions">
            <input
              type="search"
              className="form-control"
              placeholder="Search user, record, activity…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="audit-log-filters">
        <select
          className="form-select"
          value={filters.action}
          onChange={(e) => onFilterChange("action", e.target.value)}
          aria-label="Filter by activity"
        >
          <option value="">All activities</option>
          {(meta.actions || []).map((a) => (
            <option key={a} value={a}>
              {ACTION_LABELS[a] || a}
            </option>
          ))}
        </select>
        <select
          className="form-select"
          value={filters.entityType}
          onChange={(e) => onFilterChange("entityType", e.target.value)}
          aria-label="Filter by record type"
        >
          <option value="">All record types</option>
          {(meta.entityTypes || []).map((t) => (
            <option key={t} value={t}>
              {entityTypeLabel(t)}
            </option>
          ))}
        </select>
        {user?.isSuperAdmin && (
          <select
            className="form-select"
            value={filters.eventId}
            onChange={(e) => onFilterChange("eventId", e.target.value)}
            aria-label="Filter by event"
          >
            <option value="">All events</option>
            {(meta.events || []).map((ev) => (
              <option key={ev.eventId} value={ev.eventId}>
                {ev.eventName}
              </option>
            ))}
          </select>
        )}
        <input
          type="date"
          className="form-control"
          value={filters.from}
          onChange={(e) => onFilterChange("from", e.target.value)}
          title="From date"
          aria-label="From date"
        />
        <input
          type="date"
          className="form-control"
          value={filters.to}
          onChange={(e) => onFilterChange("to", e.target.value)}
          title="To date"
          aria-label="To date"
        />
      </div>

      <div className="audit-log-layout">
        <div className="audit-log-table-wrap">
          {loading ? (
            <p className="audit-log-empty">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="audit-log-empty">No audit entries match these filters.</p>
          ) : (
            <table className="audit-log-table">
              <thead>
                <tr>
                  <th>Date & time</th>
                  <th>User</th>
                  <th>Activity</th>
                  <th>Record</th>
                  <th>Fields changed</th>
                  {user?.isSuperAdmin && !filters.eventId ? <th>Event</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const changed = getChangedFields(row);
                  return (
                    <tr
                      key={row._id}
                      className={
                        String(row._id) === String(selectedId)
                          ? "is-selected"
                          : ""
                      }
                      onClick={() => setSelectedId(row._id)}
                    >
                      <td className="audit-when">{formatWhen(row.createdAt)}</td>
                      <td className="audit-user">
                        {row.actorName || "Unknown"}
                      </td>
                      <td>
                        <span className={`audit-action is-${row.action}`}>
                          {ACTION_LABELS[row.action] || row.action}
                        </span>
                      </td>
                      <td>
                        <div className="audit-record">
                          <span className="audit-record-type">
                            {entityTypeLabel(row.entityType)}
                          </span>
                          {row.entityLabel ? (
                            <span className="audit-record-name">
                              {row.entityLabel}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="audit-changed">
                        {changed.length ? (
                          <div className="audit-field-chips">
                            {changed.slice(0, 3).map((field) => (
                              <span key={field} className="audit-field-chip">
                                {formatFieldLabel(field)}
                              </span>
                            ))}
                            {changed.length > 3 ? (
                              <span className="audit-field-chip is-more">
                                +{changed.length - 3}
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <span className="audit-muted">—</span>
                        )}
                      </td>
                      {user?.isSuperAdmin && !filters.eventId ? (
                        <td className="audit-event-cell">
                          {row.eventName || "—"}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {pagination.totalPages > 1 && (
            <div className="audit-log-pager">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                disabled={pagination.page <= 1}
                onClick={() =>
                  setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))
                }
              >
                Previous
              </button>
              <span>
                Page {pagination.page} of {pagination.totalPages} ·{" "}
                {pagination.total} entries
              </span>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() =>
                  setPagination((p) => ({
                    ...p,
                    page: Math.min(p.totalPages, p.page + 1),
                  }))
                }
              >
                Next
              </button>
            </div>
          )}
        </div>

        <aside className="audit-log-detail" aria-label="Entry detail">
          <div className="audit-log-detail__head">
            <h5 className="audit-log-detail-title">Details</h5>
            {selected ? (
              <span className={`audit-action is-${selected.action}`}>
                {ACTION_LABELS[selected.action] || selected.action}
              </span>
            ) : null}
          </div>
          {!selected ? (
            <p className="audit-log-empty">Select a row to view details.</p>
          ) : (
            <div className="audit-log-detail__body">
              <p className="audit-detail-headline">
                {cleanSummary(selected.summary)}
              </p>

              <div className="audit-detail-meta">
                <div className="audit-detail-meta-row">
                  <span className="audit-detail-label">User</span>
                  <span className="audit-detail-value is-primary">
                    {selected.actorName || "Unknown"}
                    {selected.actorRole ? (
                      <span className="audit-detail-sub">
                        {" "}
                        · {selected.actorRole}
                      </span>
                    ) : null}
                  </span>
                  {selected.actorEmail ? (
                    <span className="audit-detail-sub is-block">
                      {selected.actorEmail}
                    </span>
                  ) : null}
                </div>
                <div className="audit-detail-meta-row">
                  <span className="audit-detail-label">When</span>
                  <span className="audit-detail-value">
                    {formatWhen(selected.createdAt)}
                  </span>
                </div>
                <div className="audit-detail-meta-row">
                  <span className="audit-detail-label">Record</span>
                  <span className="audit-detail-value is-primary">
                    {entityTypeLabel(selected.entityType)}
                    {selected.entityLabel
                      ? ` · ${selected.entityLabel}`
                      : ""}
                  </span>
                </div>
                {selected.eventName ? (
                  <div className="audit-detail-meta-row">
                    <span className="audit-detail-label">Event</span>
                    <span className="audit-detail-value">
                      {selected.eventName}
                    </span>
                  </div>
                ) : null}
                {selected.meta?.ip ? (
                  <div className="audit-detail-meta-row">
                    <span className="audit-detail-label">IP</span>
                    <span className="audit-detail-value is-muted">
                      {selected.meta.ip}
                    </span>
                  </div>
                ) : null}
              </div>

              {Array.isArray(selected.changes) && selected.changes.length > 0 ? (
                <div className="audit-changes-block">
                  <h6 className="audit-log-section-title">Fields changed</h6>
                  <ul className="audit-changes-list">
                    {selected.changes.map((c, idx) => (
                      <li key={`${c.field}-${idx}`}>
                        <span className="audit-change-field">
                          {formatFieldLabel(c.field)}
                        </span>
                        <div className="audit-change-values">
                          <span className="audit-from">
                            {formatChangeValue(c.from)}
                          </span>
                          <span className="audit-arrow" aria-hidden="true">
                            →
                          </span>
                          <span className="audit-to">
                            {formatChangeValue(c.to)}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : Array.isArray(selected.meta?.allowedPages) ? (
                <div className="audit-changes-block">
                  <h6 className="audit-log-section-title">Pages enabled</h6>
                  <div className="audit-field-chips">
                    {selected.meta.allowedPages.map((page) => (
                      <span key={page} className="audit-field-chip">
                        {page}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default AuditLog;
